import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog, useConfirmDialog } from "@/components/ui/confirm-dialog";
import { FilterButton } from "@/components/ui/filter-button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { filterImageFiles, uploadGameGalleryPhotos } from "@/lib/game-gallery-upload";
import { gameService } from "@/services/game.service";
import type { GameGalleryPhoto, GameGalleryResponse } from "@/types/game-gallery";
import { GameViplaySyncModal } from "@/components/games/GameViplaySyncModal";
import { Icon } from "@/components/ui/icon"
import {
  GameGalleryMediaViewer,
  GameGalleryMediaViewerDownloadButton,
  GameGalleryMediaViewerLikeButton,
} from "@/components/games/GameGalleryMediaViewer";

interface GameGalleryPanelProps {
  gameId: string;
  userId?: string;
  canManage?: boolean;
  coverImageUrl?: string | null;
}

type GalleryFilter = "ALL" | "IMAGE" | "VIDEO";

function isSameImageUrl(a?: string | null, b?: string | null) {
  if (!a || !b) return false;
  return a.split("?")[0] === b.split("?")[0];
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getDownloadFilename(photo: GameGalleryPhoto, index: number) {
  const isVideo = photo.mediaType === "VIDEO";
  return isVideo ? `jogo-lance-${index + 1}.mp4` : `jogo-foto-${index + 1}.jpg`;
}

async function downloadMedia(url: string, filename: string) {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("fetch failed");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const anchor = document.createElement("a");
    anchor.href = url.includes("dropbox") ? `${url.split("?")[0]}?dl=1` : url;
    anchor.download = filename;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}

async function downloadMultipleMedia(items: Array<{ url: string; filename: string }>) {
  for (let i = 0; i < items.length; i++) {
    await downloadMedia(items[i].url, items[i].filename);
    if (i < items.length - 1) await sleep(450);
  }
}

function GalleryPhotoItem({
  photo,
  index,
  isLoggedIn,
  canManage,
  isCover,
  selectionMode,
  selected,
  onToggleSelect,
  onLike,
  onDelete,
  onSetCover,
  isLikePending,
  isDeletePending,
  isSetCoverPending,
}: {
  photo: GameGalleryPhoto;
  index: number;
  isLoggedIn: boolean;
  canManage: boolean;
  isCover: boolean;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelect: (photoId: string) => void;
  onLike: (photoId: string) => void;
  onDelete: (photoId: string) => void;
  onSetCover: (photoId: string) => void;
  isLikePending: boolean;
  isDeletePending: boolean;
  isSetCoverPending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const isVideo = photo.mediaType === "VIDEO";
  const previewUrl = isVideo ? photo.thumbnailUrl ?? photo.url : photo.url;
  const canSetCover = canManage && !isVideo;
  const itemLabel = isVideo ? "vídeo" : "foto";
  const downloadName = getDownloadFilename(photo, index);

  const handleCardClick = () => {
    if (selectionMode) {
      onToggleSelect(photo.id);
      return;
    }
    setExpanded(true);
  };

  return (
    <>
      <div
        className={cn(
          "group relative aspect-square overflow-hidden rounded-lg bg-muted",
          selectionMode && "cursor-pointer",
          selected && "ring-2 ring-primary ring-offset-2 ring-offset-card-surface")}
      >
        <button
          type="button"
          onClick={handleCardClick}
          className="block h-full w-full cursor-pointer"
          aria-label={
            selectionMode
              ? selected
                ? `Desmarcar ${itemLabel} ${index + 1}`
                : `Selecionar ${itemLabel} ${index + 1}`
              : `Ver ${itemLabel} ${index + 1} de ${photo.userName}`
          }
        >
          <img
            src={previewUrl}
            alt={`${isVideo ? "Lance" : "Foto"} do jogo por ${photo.userName}`}
            className={cn(
              "h-full w-full object-cover transition-transform duration-300",
              !selectionMode && "group-hover:scale-105")}
            loading="lazy"
          />
          {isVideo && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
              <Icon name="play_circle" filled />
            </div>
          )}
        </button>

        {selectionMode && (
          <div
            className={cn(
              "pointer-events-none absolute left-1.5 top-1.5 z-20 flex h-6 w-6 items-center justify-center rounded-md border-2 shadow-sm transition-colors",
              selected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-white/90 bg-black/40 text-white")}
          >
            {selected && (
              <Icon name="check" />
            )}
          </div>
        )}

        {photo.source === "VIPLAY" && !selectionMode && (
          <div
            className="pointer-events-none absolute right-1.5 top-1.5 z-10 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
            title="Lance importado"
          >
            Lance
          </div>
        )}

        {isCover && !selectionMode && (
          <div className="pointer-events-none absolute left-1.5 top-1.5 z-10 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground shadow-sm">
            <Icon name="image" />
            Capa
          </div>
        )}

        {!selectionMode && (
          <>
            {photo.likedByMe && isLoggedIn && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/55 to-transparent" />
            )}

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

            <div
              className={cn(
                "absolute bottom-0 left-0 right-0 flex items-center justify-between gap-1 p-1.5 transition-opacity",
                photo.likedByMe && isLoggedIn ? "opacity-100" : "opacity-0 group-hover:opacity-100")}
            >
              <div className="pointer-events-auto flex items-center gap-0.5">
                {isLoggedIn && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onLike(photo.id);
                    }}
                    disabled={isLikePending}
                    className="h-7 gap-1 px-1.5 text-xs font-medium text-white hover:bg-white/20 hover:text-white"
                    aria-label={photo.likedByMe ? `Descurtir ${itemLabel}` : `Curtir ${itemLabel}`}
                  >
                    <Icon name="favorite" filled className={cn(photo.likedByMe && "material-icon-filled text-red-500")} />
                    {photo.likesCount > 0 ? photo.likesCount : null}
                  </Button>
                )}

                {!isLoggedIn && photo.likesCount > 0 && (
                  <span className="flex items-center gap-1 px-1.5 text-xs font-medium text-white">
                    <Icon name="favorite" filled />
                    {photo.likesCount}
                  </span>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await downloadMedia(photo.url, downloadName);
                    } catch {
                      toast.error(`Não foi possível baixar o ${itemLabel}`);
                    }
                  }}
                  className="pointer-events-auto h-7 w-7 p-0 text-white hover:bg-white/20 hover:text-white"
                  aria-label={`Baixar ${itemLabel}`}
                  title="Baixar"
                >
                  <Icon name="download" />
                </Button>
              </div>

              {canManage && (
                <div className="pointer-events-auto flex items-center gap-0.5">
                  {canSetCover && !isCover && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetCover(photo.id);
                      }}
                      disabled={isSetCoverPending}
                      className="h-7 w-7 p-0 text-white hover:bg-white/20 hover:text-white"
                      aria-label="Definir como capa do jogo"
                      title="Definir como capa"
                    >
                      <Icon name="wallpaper" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(photo.id);
                    }}
                    disabled={isDeletePending}
                    className="h-7 w-7 p-0 text-white hover:bg-destructive/80 hover:text-white"
                    aria-label={`Excluir ${itemLabel}`}
                    title="Excluir"
                  >
                    <Icon name="delete" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {expanded && !selectionMode && (
        <GameGalleryMediaViewer
          photo={photo}
          open
          onClose={() => setExpanded(false)}
          subtitle={photo.userName}
          footer={
            <>
              {isLoggedIn && (
                <GameGalleryMediaViewerLikeButton
                  photo={photo}
                  onLike={() => onLike(photo.id)}
                  isLikePending={isLikePending}
                />
              )}
              <GameGalleryMediaViewerDownloadButton
                photo={photo}
                index={index}
                onError={(message) => toast.error(message)}
              />
              {canSetCover && !isCover && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onSetCover(photo.id)}
                  disabled={isSetCoverPending}
                  className="gap-1.5"
                >
                  <Icon name="wallpaper" />
                  Usar como capa
                </Button>
              )}
              {canManage && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onDelete(photo.id)}
                  disabled={isDeletePending}
                  className="gap-1.5 text-destructive hover:text-destructive"
                >
                  <Icon name="delete" />
                  Excluir
                </Button>
              )}
            </>
          }
        />
      )}
    </>
  );
}

export function GameGalleryPanel({
  gameId,
  userId,
  canManage = false,
  coverImageUrl,
}: GameGalleryPanelProps) {
  const queryClient = useQueryClient();
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [viplaySyncOpen, setViplaySyncOpen] = useState(false);
  const [mediaFilter, setMediaFilter] = useState<GalleryFilter>("ALL");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const deleteConfirmDialog = useConfirmDialog();
  const bulkDeleteConfirmDialog = useConfirmDialog();
  const setCoverConfirmDialog = useConfirmDialog();

  const galleryQuery = useQuery({
    queryKey: ["gameGallery", gameId, userId],
    queryFn: () => gameService.getGalleryPhotos(gameId),
    enabled: !!gameId,
    staleTime: 1000 * 30,
  });

  const photos = galleryQuery.data?.photos ?? [];
  const isLoggedIn = !!userId;

  const counts = useMemo(
    () => ({
      all: photos.length,
      images: photos.filter((p) => p.mediaType !== "VIDEO").length,
      videos: photos.filter((p) => p.mediaType === "VIDEO").length,
    }),
    [photos],
  );

  const filteredPhotos = useMemo(() => {
    if (mediaFilter === "IMAGE") return photos.filter((p) => p.mediaType !== "VIDEO");
    if (mediaFilter === "VIDEO") return photos.filter((p) => p.mediaType === "VIDEO");
    return photos;
  }, [photos, mediaFilter]);

  const selectedPhotos = useMemo(
    () => photos.filter((p) => selectedIds.has(p.id)),
    [photos, selectedIds],
  );

  const selectedInFilter = useMemo(
    () => filteredPhotos.filter((p) => selectedIds.has(p.id)),
    [filteredPhotos, selectedIds],
  );

  const likeMutation = useMutation({
    mutationFn: (photoId: string) => gameService.toggleGalleryPhotoLike(gameId, photoId),
    onMutate: async (photoId) => {
      await queryClient.cancelQueries({ queryKey: ["gameGallery", gameId, userId] });
      const previous = queryClient.getQueryData<GameGalleryResponse>(["gameGallery", gameId, userId]);
      queryClient.setQueryData<GameGalleryResponse>(["gameGallery", gameId, userId], (old) => {
        if (!old) return old;
        return {
          photos: old.photos.map((p) =>
            p.id === photoId
              ? {
                  ...p,
                  likedByMe: !p.likedByMe,
                  likesCount: p.likedByMe ? Math.max(0, p.likesCount - 1) : p.likesCount + 1,
                }
              : p,
          ),
        };
      });
      return { previous };
    },
    onError: (error: any, _photoId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["gameGallery", gameId, userId], context.previous);
      }
      toast.error(error?.response?.data?.message || "Não foi possível curtir a foto");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["gameGallery", gameId, userId] });
      await queryClient.invalidateQueries({ queryKey: ["myGameGalleries"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (photoId: string) => gameService.deleteGalleryPhoto(gameId, photoId),
    onMutate: (photoId) => {
      const cached = queryClient.getQueryData<GameGalleryResponse>(["gameGallery", gameId, userId]);
      const photo = cached?.photos.find((p) => p.id === photoId);
      return { isVideo: photo?.mediaType === "VIDEO" };
    },
    onSuccess: async (_data, _photoId, context) => {
      await queryClient.invalidateQueries({ queryKey: ["gameGallery", gameId, userId] });
      await queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
      await queryClient.invalidateQueries({ queryKey: ["myGameGalleries"] });
      toast.success(context?.isVideo ? "Lance removido" : "Foto removida");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Não foi possível excluir");
    },
  });

  const setCoverMutation = useMutation({
    mutationFn: (photoId: string) => gameService.setCoverFromGalleryPhoto(gameId, photoId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
      await queryClient.invalidateQueries({ queryKey: ["gameGallery", gameId, userId] });
      toast.success("Capa do jogo atualizada");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Não foi possível definir a capa");
    },
  });

  const toggleSelection = (photoId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleFilterChange = (filter: GalleryFilter) => {
    setMediaFilter(filter);
    setSelectedIds(new Set());
  };

  const handleSelectAllFiltered = () => {
    setSelectedIds(new Set(filteredPhotos.map((p) => p.id)));
  };

  const handleBulkDownload = async () => {
    if (selectedPhotos.length === 0) return;
    setBulkWorking(true);
    try {
      const items = selectedPhotos.map((photo, i) => ({
        url: photo.url,
        filename: getDownloadFilename(photo, i),
      }));
      await downloadMultipleMedia(items);
      toast.success(
        selectedPhotos.length === 1
          ? "Download iniciado"
          : `${selectedPhotos.length} downloads iniciados`,
      );
    } catch {
      toast.error("Não foi possível baixar os itens selecionados");
    } finally {
      setBulkWorking(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedPhotos.length === 0) return;
    const videoCount = selectedPhotos.filter((p) => p.mediaType === "VIDEO").length;
    const photoCount = selectedPhotos.length - videoCount;
    const parts: string[] = [];
    if (photoCount > 0) parts.push(`${photoCount} foto(s)`);
    if (videoCount > 0) parts.push(`${videoCount} lance(s)`);

    bulkDeleteConfirmDialog.open({
      title: `Excluir ${selectedPhotos.length} item(ns)?`,
      description: `Remove ${parts.join(" e ")} da galeria. Essa ação não pode ser desfeita.`,
      variant: "danger",
      confirmText: "Excluir selecionados",
      onConfirm: async () => {
        setBulkWorking(true);
        let deleted = 0;
        let failed = 0;
        try {
          for (const photo of selectedPhotos) {
            try {
              await gameService.deleteGalleryPhoto(gameId, photo.id);
              deleted++;
            } catch {
              failed++;
            }
          }
          await queryClient.invalidateQueries({ queryKey: ["gameGallery", gameId, userId] });
          await queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
          exitSelectionMode();
          if (deleted > 0 && failed === 0) {
            toast.success(`${deleted} item(ns) removido(s) da galeria`);
          } else if (deleted > 0) {
            toast.warning(`${deleted} removido(s), ${failed} falharam`);
          } else {
            toast.error("Não foi possível excluir os itens selecionados");
          }
        } finally {
          setBulkWorking(false);
        }
      },
    });
  };

  const handleUploadFiles = async (files: File[]) => {
    const imageFiles = filterImageFiles(files);
    if (imageFiles.length === 0) {
      toast.error("Por favor, selecione apenas arquivos de imagem");
      return;
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: imageFiles.length });
    try {
      const { uploaded, failed } = await uploadGameGalleryPhotos(gameId, imageFiles, (done, total) => {
        setUploadProgress({ done, total });
      });
      await queryClient.invalidateQueries({ queryKey: ["gameGallery", gameId, userId] });
      await queryClient.invalidateQueries({ queryKey: ["myGameGalleries"] });

      if (uploaded > 0 && failed === 0) {
        toast.success(uploaded === 1 ? "Foto adicionada à galeria" : `${uploaded} fotos adicionadas à galeria`);
      } else if (uploaded > 0) {
        toast.warning(`${uploaded} foto(s) enviada(s), ${failed} falharam`);
      } else {
        toast.error("Não foi possível enviar as fotos");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Erro ao adicionar fotos");
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleDelete = (photoId: string) => {
    const photo = photos.find((p) => p.id === photoId);
    const isVideo = photo?.mediaType === "VIDEO";
    deleteConfirmDialog.open({
      title: isVideo ? "Excluir lance?" : "Excluir foto?",
      description: isVideo
        ? "Remove o lance da galeria do jogo."
        : "Essa ação remove a foto da galeria e não poderá ser desfeita.",
      variant: "danger",
      confirmText: "Excluir",
      onConfirm: async () => {
        await deleteMutation.mutateAsync(photoId);
      },
    });
  };

  const handleSetCover = (photoId: string) => {
    setCoverConfirmDialog.open({
      title: "Definir como capa do jogo?",
      description: "Esta foto será exibida como imagem principal do jogo nos cards e no topo da página.",
      variant: "default",
      confirmText: "Definir capa",
      onConfirm: async () => {
        await setCoverMutation.mutateAsync(photoId);
      },
    });
  };

  const showFilters = counts.all > 0 && (counts.images > 0 || counts.videos > 0);

  const showSelectButton = photos.length > 0;
  const showActionButtons = showSelectButton || canManage || isLoggedIn;

  const compactFilterClass =
    "h-9 min-w-0 w-full justify-center gap-1 rounded-lg px-1.5 sm:h-10 sm:rounded-xl [&>span:nth-child(1)]:text-[16px] [&>span:nth-child(2)]:truncate [&>span:nth-child(2)]:text-[11px] sm:[&>span:nth-child(2)]:text-xs [&>span:nth-child(3)]:px-1.5 [&>span:nth-child(3)]:text-[10px]";

  const galleryActionButtons = showActionButtons ? (
    <div className="mt-4 grid grid-cols-3 gap-1.5">
      {showSelectButton && (
        <Button
          type="button"
          variant={selectionMode ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (selectionMode) exitSelectionMode();
            else setSelectionMode(true);
          }}
          className="h-9 min-w-0 justify-center gap-1 rounded-lg px-1.5 text-[11px] font-semibold sm:h-10 sm:rounded-xl sm:text-xs"
        >
          <Icon name={selectionMode ? "close" : "checklist"} />
          <span className="truncate">{selectionMode ? "Cancelar" : "Selecionar"}</span>
        </Button>
      )}

      {canManage && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setViplaySyncOpen(true)}
          title="Importar lances"
          className="h-9 min-w-0 justify-center gap-1 rounded-lg px-1.5 text-[11px] font-semibold sm:h-10 sm:rounded-xl sm:text-xs"
        >
          <Icon name="videocam" />
          <span className="truncate">Importar</span>
        </Button>
      )}

      {isLoggedIn && (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => !uploading && galleryInputRef.current?.click()}
            disabled={uploading}
            className="h-9 min-w-0 justify-center gap-1 rounded-lg px-1.5 text-[11px] font-semibold sm:h-10 sm:rounded-xl sm:text-xs"
          >
            {uploading ? (
              <>
                <Icon name="progress_activity" />
                <span className="truncate">
                  {uploadProgress && uploadProgress.total > 1
                    ? `${uploadProgress.done}/${uploadProgress.total}`
                    : "Enviando"}
                </span>
              </>
            ) : (
              <>
                <Icon name="add_photo_alternate" />
                <span className="truncate">Adicionar</span>
              </>
            )}
          </Button>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            disabled={uploading}
            onChange={async (e) => {
              const files = e.target.files;
              if (!files?.length) return;
              await handleUploadFiles(Array.from(files));
              e.target.value = "";
            }}
          />
        </>
      )}
    </div>
  ) : null;

  return (
    <div
      id="galeria"
      className="w-full bg-card-surface rounded-xl border border-card-border p-6 shadow-xl shadow-black/5 dark:shadow-none md:p-8 scroll-mt-24"
    >
      <div className="mb-4 flex items-center gap-2 shrink-0">
        <Icon name="photo_library" />
        <h3 className="text-xl font-bold text-foreground">Galeria</h3>
        {photos.length > 0 && (
          <span className="text-sm font-medium text-muted-foreground">({photos.length})</span>
        )}
      </div>

      {showFilters && (
        <div className="mb-4 grid grid-cols-3 gap-1.5">
          <FilterButton
            icon="grid_view"
            label="Tudo"
            badge={counts.all}
            active={mediaFilter === "ALL"}
            variant="primary"
            onClick={() => handleFilterChange("ALL")}
            className={compactFilterClass}
          />
          {counts.images > 0 && (
            <FilterButton
              icon="photo"
              label="Fotos"
              badge={counts.images}
              active={mediaFilter === "IMAGE"}
              variant="primary"
              onClick={() => handleFilterChange("IMAGE")}
              className={compactFilterClass}
            />
          )}
          {counts.videos > 0 && (
            <FilterButton
              icon="videocam"
              label="Lances"
              badge={counts.videos}
              active={mediaFilter === "VIDEO"}
              variant="primary"
              onClick={() => handleFilterChange("VIDEO")}
              className={compactFilterClass}
            />
          )}
        </div>
      )}

      {selectionMode && filteredPhotos.length > 0 && (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {selectedInFilter.length} selecionado(s)
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSelectAllFiltered}
              disabled={bulkWorking || selectedInFilter.length === filteredPhotos.length}
              className="h-8 text-xs font-semibold"
            >
              Selecionar todos
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:ml-auto sm:flex sm:flex-wrap sm:items-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleBulkDownload}
              disabled={bulkWorking || selectedPhotos.length === 0}
              className="h-9 w-full justify-center gap-1.5 text-xs font-semibold sm:w-auto"
            >
              <Icon name="download" />
              Baixar
            </Button>
            {canManage && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleBulkDelete}
                disabled={bulkWorking || selectedPhotos.length === 0}
                className="h-9 w-full justify-center gap-1.5 text-xs font-semibold text-destructive hover:text-destructive sm:w-auto"
              >
                <Icon name="delete" />
                Excluir
              </Button>
            )}
          </div>
        </div>
      )}

      {galleryQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 py-10 text-center">
          <Icon name="photo_library" />
          <p className="text-sm font-medium text-muted-foreground">
            {isLoggedIn
              ? "Nenhuma mídia ainda. Envie fotos ou importe lances."
              : "Nenhuma foto ou lance na galeria ainda."}
          </p>
        </div>
      ) : filteredPhotos.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 py-10 text-center">
          <Icon name={mediaFilter === "VIDEO" ? "videocam_off" : "hide_image"} />
          <p className="text-sm font-medium text-muted-foreground">
            {mediaFilter === "VIDEO" ? "Nenhum lance nesta galeria." : "Nenhuma foto nesta galeria."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filteredPhotos.map((photo, index) => (
            <GalleryPhotoItem
              key={photo.id}
              photo={photo}
              index={index}
              isLoggedIn={isLoggedIn}
              canManage={canManage}
              isCover={isSameImageUrl(photo.url, coverImageUrl)}
              selectionMode={selectionMode}
              selected={selectedIds.has(photo.id)}
              onToggleSelect={toggleSelection}
              onLike={(photoId) => likeMutation.mutate(photoId)}
              onDelete={handleDelete}
              onSetCover={handleSetCover}
              isLikePending={likeMutation.isPending}
              isDeletePending={deleteMutation.isPending}
              isSetCoverPending={setCoverMutation.isPending}
            />
          ))}
        </div>
      )}

      {galleryActionButtons}

      <GameViplaySyncModal
        gameId={gameId}
        open={viplaySyncOpen}
        onOpenChange={setViplaySyncOpen}
        onSynced={async () => {
          await queryClient.invalidateQueries({ queryKey: ["gameGallery", gameId, userId] });
        }}
      />

      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={deleteConfirmDialog.close}
        onConfirm={deleteConfirmDialog.handleConfirm}
        title={deleteConfirmDialog.config?.title ?? ""}
        description={deleteConfirmDialog.config?.description ?? ""}
        confirmText={deleteConfirmDialog.config?.confirmText ?? "Excluir"}
        variant={deleteConfirmDialog.config?.variant ?? "danger"}
        isLoading={deleteConfirmDialog.isLoading}
      />
      <ConfirmDialog
        isOpen={bulkDeleteConfirmDialog.isOpen}
        onClose={bulkDeleteConfirmDialog.close}
        onConfirm={bulkDeleteConfirmDialog.handleConfirm}
        title={bulkDeleteConfirmDialog.config?.title ?? ""}
        description={bulkDeleteConfirmDialog.config?.description ?? ""}
        confirmText={bulkDeleteConfirmDialog.config?.confirmText ?? "Excluir"}
        variant={bulkDeleteConfirmDialog.config?.variant ?? "danger"}
        isLoading={bulkDeleteConfirmDialog.isLoading || bulkWorking}
      />
      <ConfirmDialog
        isOpen={setCoverConfirmDialog.isOpen}
        onClose={setCoverConfirmDialog.close}
        onConfirm={setCoverConfirmDialog.handleConfirm}
        title={setCoverConfirmDialog.config?.title ?? ""}
        description={setCoverConfirmDialog.config?.description ?? ""}
        confirmText={setCoverConfirmDialog.config?.confirmText ?? "Definir capa"}
        variant={setCoverConfirmDialog.config?.variant ?? "default"}
        isLoading={setCoverConfirmDialog.isLoading}
      />
    </div>
  );
}
